import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import SEO from '../../components/SEO';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch product:', err);
      setError('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    try {
      await api.post('/cart/items', {
        product_id: product.id,
        quantity: quantity
      });
      alert(`${quantity} item(s) added to cart!`);
    } catch (err) {
      if (err.response?.status === 401) {
        alert('Please login to add items to cart');
        navigate('/login');
      } else if (err.response?.status === 400) {
        alert(err.response.data.error || 'Failed to add product to cart');
      } else {
        alert('Failed to add product to cart');
      }
    }
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 1 && value <= product.stock) {
      setQuantity(value);
    }
  };

  if (loading) return <div className="loading">Loading product...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!product) return <div className="error">Product not found</div>;

  return (
    <>
      <SEO
        title={product.name}
        description={product.description || `Buy ${product.name}`}
        keywords={`${product.name}, ${product.category}, product, shop`}
      />
      <div className="product-detail-page">
        <div className="container">
          <button className="back-button" onClick={() => navigate('/products')}>
            ← Back to Products
          </button>

          <div className="product-detail">
            <div className="product-image-large">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} />
              ) : (
                <div className="placeholder-image-large">
                  <span>No Image Available</span>
                </div>
              )}
            </div>

            <div className="product-details">
              {product.category && (
                <span className="product-category-badge">{product.category}</span>
              )}
              <h1>{product.name}</h1>

              {product.sku && (
                <p className="product-sku">SKU: {product.sku}</p>
              )}

              <div className="product-price-large">
                ${parseFloat(product.price).toFixed(2)}
              </div>

              {product.description && (
                <div className="product-description-full">
                  <h3>Description</h3>
                  <p>{product.description}</p>
                </div>
              )}

              <div className="product-stock-info">
                {product.stock > 0 ? (
                  <>
                    <span className="in-stock">In Stock ({product.stock} available)</span>
                    {product.stock <= 10 && (
                      <span className="low-stock-warning">Hurry! Only {product.stock} left</span>
                    )}
                  </>
                ) : (
                  <span className="out-of-stock">Out of Stock</span>
                )}
              </div>

              {product.stock > 0 && (
                <div className="quantity-selector">
                  <label htmlFor="quantity">Quantity:</label>
                  <input
                    type="number"
                    id="quantity"
                    min="1"
                    max={product.stock}
                    value={quantity}
                    onChange={handleQuantityChange}
                  />
                </div>
              )}

              <div className="product-actions-large">
                <button
                  className="btn btn-primary btn-large"
                  onClick={addToCart}
                  disabled={product.stock <= 0}
                >
                  {product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
                </button>
                <button
                  className="btn btn-secondary btn-large"
                  onClick={() => navigate('/products')}
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetail;
